import React, { useState, useEffect } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material'
import { HiOutlineKey, HiOutlineCheckCircle, HiOutlineBan, HiOutlineRefresh } from 'react-icons/hi'
import { fetchKiosks, generateKioskCredential, activateKiosk, deactivateKiosk } from '../../services/KioskService'
import KioskRegisterModal from './KioskRegisterModal'
import KioskCredentialDialog from './KioskCredentialDialog'

// Comfortably above the backend's 90s online timeout so a manual/auto refresh
// never fires mid-flip; a leave-it-open monitoring screen still stays current.
const AUTO_REFRESH_MS = 30000

const KioskList = () => {
    const DEFAULT_PAGE_SIZE = 10
    const [kiosks, setKiosks] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectionModel, setSelectionModel] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [connectivityFilter, setConnectivityFilter] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const [refresh, setRefresh] = useState(false)
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false)
    const [actionLoadingId, setActionLoadingId] = useState(null)
    const [credentialDialog, setCredentialDialog] = useState({ open: false, kioskCode: '', secret: '' })
    const [deactivateTarget, setDeactivateTarget] = useState(null)
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)

    const columns = [
        { field: 'kioskCode', headerName: 'Kiosk Code', width: 150 },
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'location', headerName: 'Location', width: 200 },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => {
                const isActive = params.value === 'ACTIVE'

                return <span className={isActive ? 'badge-success' : 'badge-danger'}>{params.value}</span>
            }
        },
        {
            field: 'connectivity',
            headerName: 'Connectivity',
            width: 140,
            renderCell: (params) => {
                const isOnline = params.value === 'ONLINE'
                return (
                    <span className="flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {params.value}
                    </span>
                )
            }
        },
        {
            field: 'lastSeenAt',
            headerName: 'Last Seen',
            width: 180,
            valueFormatter: (value) => (value ? new Date(value).toLocaleString() : 'Never')
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row
                const isBusy = actionLoadingId === row.id
                const isActive = row.status === 'ACTIVE'

                return (
                    <div className="flex items-center gap-1">
                        <Tooltip title="Generate Credential">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isBusy}
                                    onClick={() => handleGenerateCredential(row)}
                                >
                                    <HiOutlineKey />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isActive ? 'Already Active' : 'Activate'}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isBusy || isActive}
                                    onClick={() => handleActivate(row)}
                                >
                                    <HiOutlineCheckCircle />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isActive ? 'Deactivate' : 'Already Inactive'}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isBusy || !isActive}
                                    onClick={() => setDeactivateTarget(row)}
                                >
                                    <HiOutlineBan />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                )
            }
        }
    ]

    useEffect(() => {
        fetchKioskList(searchTerm, connectivityFilter, page, pageSize)
    }, [searchTerm, connectivityFilter, page, pageSize, refresh])

    // Connectivity is computed server-side at read time (never stored), so a plain
    // refetch is all a "refresh" needs — no separate connectivity-check call.
    useEffect(() => {
        const interval = setInterval(() => setRefresh((prev) => !prev), AUTO_REFRESH_MS)
        return () => clearInterval(interval)
    }, [])

    const fetchKioskList = async (search, connectivity, page, limit) => {
        setLoading(true)
        try {
            const filter = { page, limit, search, connectivity: connectivity || undefined }
            const response = await fetchKiosks(filter)
            const records = response.data || []
            setTotalCount(response.count || 0)
            const formattedData = records.map((record) => ({
                id: record.id,
                kioskCode: record.kioskCode,
                name: record.name,
                location: record.location,
                status: record.status,
                connectivity: record.connectivity,
                lastSeenAt: record.lastSeenAt
            }))
            setKiosks(formattedData)
        } catch (error) {
            console.error('Error fetching kiosk list:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value)
    }

    const handleConnectivityFilterChange = (event) => {
        setConnectivityFilter(event.target.value)
    }

    const handleManualRefresh = () => {
        setRefresh((prev) => !prev)
    }

    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true)
    }

    const handleCloseRegisterDialog = () => {
        setRegisterDialogOpen(false)
    }

    const handleGenerateCredential = async (row) => {
        setActionLoadingId(row.id)
        try {
            const result = await generateKioskCredential(row.id)
            if (result.error) {
                setResponseMessage(result.error)
                setShowResponseDialog(true)
            } else {
                setCredentialDialog({ open: true, kioskCode: row.kioskCode, secret: result.secret })
            }
        } finally {
            setActionLoadingId(null)
        }
    }

    const handleActivate = async (row) => {
        setActionLoadingId(row.id)
        try {
            const result = await activateKiosk(row.id)
            setResponseMessage(result.error || result.message || `${row.kioskCode} activated`)
            setShowResponseDialog(true)
            setRefresh((prev) => !prev)
        } finally {
            setActionLoadingId(null)
        }
    }

    const handleConfirmDeactivate = async () => {
        const row = deactivateTarget
        setDeactivateTarget(null)
        setActionLoadingId(row.id)
        try {
            const result = await deactivateKiosk(row.id)
            setResponseMessage(result.error || result.message || `${row.kioskCode} deactivated`)
            setShowResponseDialog(true)
            setRefresh((prev) => !prev)
        } finally {
            setActionLoadingId(null)
        }
    }

    const handleCloseCredentialDialog = () => {
        setCredentialDialog({ open: false, kioskCode: '', secret: '' })
    }

    return (
        <div className="page-container">
            <div className="page-toolbar">
                <h1 className="page-title">Kiosks</h1>
            </div>

            {/* Search, Filter, Refresh, and Register Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search..."
                    className="input-field sm:max-w-xs"
                    value={searchTerm}
                    onChange={handleSearchChange}
                />

                <select
                    value={connectivityFilter}
                    onChange={handleConnectivityFilterChange}
                    className="input-field sm:w-56 whitespace-nowrap"
                >
                    <option value="">All Connectivity</option>
                    <option value="ONLINE">Online</option>
                    <option value="OFFLINE">Offline</option>
                </select>

                <Tooltip title="Refresh">
                    <IconButton onClick={handleManualRefresh}>
                        <HiOutlineRefresh />
                    </IconButton>
                </Tooltip>

                <button onClick={handleOpenRegisterDialog} className="btn-primary sm:ml-auto whitespace-nowrap">
                    Register Kiosk
                </button>
            </div>

            {/* Kiosk DataGrid */}
            <Paper
                sx={{
                    width: '100%',
                    height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 260px)' },
                    minWidth: 0
                }}
            >
                <DataGrid
                    rows={kiosks}
                    columns={columns.map((column) => ({ ...column, flex: 1 }))}
                    loading={loading}
                    page={page - 1}
                    pageSize={pageSize}
                    rowCount={totalCount}
                    paginationMode="server"
                    initialState={{
                        pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } }
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    onPaginationModelChange={(newPaginationModel) => {
                        setPage(newPaginationModel.page + 1)
                        setPageSize(newPaginationModel.pageSize)
                    }}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    selectionModel={selectionModel}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'default'
                        }
                    }}
                />
            </Paper>

            <KioskRegisterModal
                open={registerDialogOpen}
                onClose={handleCloseRegisterDialog}
                onRegisterSuccess={() => setRefresh((prev) => !prev)}
            />

            <KioskCredentialDialog
                open={credentialDialog.open}
                onClose={handleCloseCredentialDialog}
                kioskCode={credentialDialog.kioskCode}
                secret={credentialDialog.secret}
            />

            {/* Deactivate confirmation */}
            <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Deactivate Kiosk {deactivateTarget?.kioskCode}?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        Deactivating revokes this kiosk's active sessions and credential immediately. Reactivating it
                        afterward will not restore access — a new credential must be generated before it can log in
                        again.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeactivateTarget(null)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDeactivate} color="error" variant="contained">
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Response Dialog */}
            <Dialog open={showResponseDialog} onClose={() => setShowResponseDialog(false)}>
                <DialogTitle>Response</DialogTitle>
                <DialogContent>
                    <p>{responseMessage}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResponseDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default KioskList
